/**
 * useTimeout Hook
 * Tracks operation duration and triggers callbacks at specific time thresholds
 * Provides user feedback for long-running operations (10s+)
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseTimeoutOptions {
  /**
   * Callback triggered at 10 seconds
   */
  onWarning?: () => void;

  /**
   * Callback triggered at 15 seconds
   */
  onExtended?: () => void;

  /**
   * Callback triggered at 30 seconds (timeout)
   */
  onTimeout?: () => void;

  /**
   * Whether to automatically cancel at 30 seconds
   * @default true
   */
  autoCancelAt30s?: boolean;
}

export interface UseTimeoutResult {
  /**
   * Current elapsed time in seconds
   */
  elapsed: number;

  /**
   * Whether the operation has timed out (30s)
   */
  hasTimedOut: boolean;

  /**
   * Whether a warning has been shown (10s)
   */
  hasWarning: boolean;

  /**
   * Whether extended time message has been shown (15s)
   */
  hasExtended: boolean;

  /**
   * Start tracking time
   */
  start: () => void;

  /**
   * Stop tracking time and reset
   */
  stop: () => void;

  /**
   * Reset the timer
   */
  reset: () => void;
}

/**
 * Hook for tracking long-running operations with automatic warnings
 *
 * Provides time tracking with callbacks at specific thresholds:
 * - 10s: Warning - "This is taking longer than expected..."
 * - 15s: Extended - "Still working on it..."
 * - 30s: Timeout - "Operation timed out"
 *
 * @example
 * ```tsx
 * const { start, stop, hasWarning, hasExtended, hasTimedOut } = useTimeout({
 *   onWarning: () => showToast('This is taking longer than expected...'),
 *   onExtended: () => showToast('Still working on it...'),
 *   onTimeout: () => {
 *     showToast('Operation timed out', { isError: true });
 *     cancelOperation();
 *   },
 * });
 *
 * const handleExport = async () => {
 *   start();
 *   try {
 *     await exportData();
 *   } finally {
 *     stop();
 *   }
 * };
 * ```
 */
export function useTimeout(options: UseTimeoutOptions = {}): UseTimeoutResult {
  const {
    onWarning,
    onExtended,
    onTimeout,
    autoCancelAt30s = true,
  } = options;

  const [elapsed, setElapsed] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  const [hasWarning, setHasWarning] = useState(false);
  const [hasExtended, setHasExtended] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const intervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  const start = useCallback(() => {
    setIsTracking(true);
    setElapsed(0);
    setHasWarning(false);
    setHasExtended(false);
    setHasTimedOut(false);
    startTimeRef.current = Date.now();
  }, []);

  const stop = useCallback(() => {
    setIsTracking(false);
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setElapsed(0);
    setHasWarning(false);
    setHasExtended(false);
    setHasTimedOut(false);
  }, []);

  const reset = useCallback(() => {
    setElapsed(0);
    setHasWarning(false);
    setHasExtended(false);
    setHasTimedOut(false);
    if (isTracking) {
      startTimeRef.current = Date.now();
    }
  }, [isTracking]);

  useEffect(() => {
    if (!isTracking) {
      return;
    }

    intervalRef.current = window.setInterval(() => {
      const currentElapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(currentElapsed);

      // Trigger warning at 10s
      if (currentElapsed >= 10 && !hasWarning) {
        setHasWarning(true);
        onWarning?.();
      }

      // Trigger extended message at 15s
      if (currentElapsed >= 15 && !hasExtended) {
        setHasExtended(true);
        onExtended?.();
      }

      // Trigger timeout at 30s
      if (currentElapsed >= 30 && !hasTimedOut) {
        setHasTimedOut(true);
        onTimeout?.();
        if (autoCancelAt30s) {
          setIsTracking(false);
          if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }
    }, 1000);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isTracking, hasWarning, hasExtended, hasTimedOut, onWarning, onExtended, onTimeout, autoCancelAt30s]);

  return {
    elapsed,
    hasTimedOut,
    hasWarning,
    hasExtended,
    start,
    stop,
    reset,
  };
}
